// Chips, dots, product tags, status pills, tabs, the flash and the empty, loading and problem states.
import { Link } from '../../lib/router.jsx'
import { productDot, statusTone } from './format.js'
import { Button } from './Page.jsx'

// A filter or pick chip. pressed draws it black. dot: a product (its colour) or a colour token.
//   <Chip pressed={status === 'Active'} onClick={...}>Active</Chip>
//   <Chip pressed={...} product={p} onClick={...}>{p.name}</Chip>
export function Chip({ pressed = false, product, dot, tall = false, children, className = '', ...rest }) {
  const colour = product ? productDot(product) : dot
  return (
    <button type="button" className={`ad-chip${tall ? ' ad-chip--tall' : ''} ${className}`.trim()} aria-pressed={pressed} {...rest}>
      {colour && <Dot colour={colour} />}
      {children}
    </button>
  )
}

// A row of chips with its label: <Chips label="Product">{...}</Chips>
export function Chips({ label, children, className = '' }) {
  return (
    <div className={`ad-chips ${className}`.trim()} role="group" aria-label={label}>
      {label && <span className="ad-chips-label" aria-hidden="true">{label}</span>}
      {children}
    </div>
  )
}

// A product's dot: <Dot product={p} /> or <Dot colour="var(--shell-gold)" />
export function Dot({ product, colour, ringed = false, size }) {
  const style = { background: colour || productDot(product) }
  if (size) Object.assign(style, { width: size, height: size, borderRadius: size / 2 })
  return <span className={`ad-dot${ringed ? ' ad-dot--ringed' : ''}`} style={style} aria-hidden="true" />
}

// A product's grey tag with its dot, as People's Products column draws it
export function ProductTag({ product, children }) {
  return <span className="ad-tag"><Dot product={product} size={7} />{children || product?.name}</span>
}

// A status pill. tone: attention (black edge), paused (grey edge), ended (faint), active (grey fill);
// or status: an access status or People's status_key, which picks the tone.
export function StatusPill({ tone, status, children }) {
  const t = tone || statusTone(status)
  return <span className={`ad-pill${t === 'active' ? '' : ` ad-pill--${t}`}`}>{children}</span>
}

// Tabs under a page's heading, as Recordings and dates draws them.
//   <Tabs label="Recordings and dates" current="dates" items={[
//     { key: 'recordings', label: 'Recordings', to: '/admin/recordings' },
//     { key: 'dates', label: 'Cohorts and dates', to: '/admin/recordings/dates' },
//     { key: 'webinars', label: 'Webinars', to: '/admin/webinars' },
//   ]} />
// An item with onClick instead of to is a button (a tab within the page).
export function Tabs({ items, current, label }) {
  return (
    <nav className="ad-tabs" aria-label={label}>
      {items.map(item => (item.to
        ? <Link key={item.key} to={item.to} className="ad-tab" aria-current={item.key === current ? 'page' : undefined}>{item.label}</Link>
        : <button key={item.key} type="button" className="ad-tab" aria-current={item.key === current ? 'page' : undefined} onClick={item.onClick}>{item.label}</button>))}
    </nav>
  )
}

// What just happened, with the gold bar on the left: "Done. 3 people have the masterclass now."
// actions: buttons beside it (Undo this batch). tone 'problem' makes it an alert.
export function Flash({ children, actions, tone = 'done', onClose, closeLabel = 'Close' }) {
  if (!children) return null
  return (
    <div className="ad-flash" role={tone === 'problem' ? 'alert' : 'status'}>
      <span className="ad-flash-text">{children}</span>
      {(actions || onClose) && (
        <span className="ad-flash-actions">
          {actions}
          {onClose && <Button size="sm" onClick={onClose}>{closeLabel}</Button>}
        </span>
      )}
    </div>
  )
}

// While a read is on its way
export function Loading({ children = 'Loading.', className = '' }) {
  return <p className={`ad-loading ${className}`.trim()} aria-busy="true" role="status">{children}</p>
}

// Nothing to show: "Nobody matches that. Clear the filters to see everyone."
export function EmptyState({ children, action, className = '' }) {
  return (
    <div className={className}>
      <p className="ad-empty">{children}</p>
      {action && <div className="ad-empty-action">{action}</div>}
    </div>
  )
}

// A read or a write that didn't work, with its words (an AdminError's message) and Try again
export function Problem({ error, children, onRetry, retryLabel = 'Try again' }) {
  return (
    <div className="ad-problem" role="alert">
      <p className="ad-problem-text">{children || error?.message || "That didn't load. Check your connection and try again."}</p>
      {onRetry && <Button size="sm" onClick={onRetry}>{retryLabel}</Button>}
    </div>
  )
}
